'use client';

import { startTransition, useOptimistic } from 'react';

import type { PostCommentSearchItem } from '@ps/api/types';
import { useCurrentUserId } from '@ps/ui/contexts/auth';
import { useCurrentUserProfile } from '@ps/ui/contexts/user-profile';

import { createComment } from '../mutations/createComment';
import { usePostComments } from './usePostComments';

/**
 * Paginated root comments plus an optimistic create, built on
 * React 19 `useOptimistic`. The server-confirmed list comes
 * from `usePostComments`; `submit(text)` prepends a temporary
 * row inside a transition (so it paints immediately) and calls
 * `createComment`. On success the confirmed list is updated
 * with the real id — the transition settles onto it with no
 * flicker; on failure the optimistic row is discarded when the
 * transition settles (auto-revert) and `submit`'s promise
 * rejects so the input can restore the text and surface an
 * error. `submit` resolves without doing anything for an
 * anonymous user (the input handles the login redirect).
 *
 * `isSubmitting` is a second `useOptimistic` flag flipped true
 * for the span of the action (both optimistic values paint
 * together, then revert when it settles) — the single source of
 * "a create is in flight", handed to the input rather than
 * tracked twice.
 */
export function useOptimisticComments(
  sellerId: string,
  postId: string,
  onCommentCreated?: () => void,
) {
  const currentUserId = useCurrentUserId();
  const currentProfile = useCurrentUserProfile();
  const { data, isInitializing, hasMore, loadMore, updateData } =
    usePostComments(sellerId, postId);

  // Newest-first (server sorts `createdAt` desc), so prepend.
  const [comments, addOptimistic] = useOptimistic(
    data,
    (list: PostCommentSearchItem[], pending: PostCommentSearchItem) => [
      pending,
      ...list,
    ],
  );
  const [isSubmitting, setSubmitting] = useOptimistic(
    false,
    (_previous: boolean, submitting: boolean) => submitting,
  );

  const submit = (text: string): Promise<void> =>
    new Promise((resolve, reject) => {
      if (!currentUserId) {
        resolve();
        return;
      }

      const tempId = `temp-${Date.now()}`;
      const pending: PostCommentSearchItem = {
        id: tempId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        level: 0,
        text,
        userId: currentUserId,
        profile: {
          id: currentUserId,
          urlCode: currentProfile?.urlCode ?? '',
          storeFrontName: currentProfile?.storeFrontName,
          avatar: currentProfile?.avatar,
          // Required by `BaseProfileItem` but never rendered here
          // (the row shows `storeFrontName ?? urlCode`); the next
          // `usePostComments` refetch fills them in.
          firstName: currentProfile?.firstName ?? '',
          lastName: currentProfile?.lastName ?? '',
        } as PostCommentSearchItem['profile'],
        isFollower: false,
        likeCount: 0,
        replyCount: 0,
        removed: false,
        relations: {} as PostCommentSearchItem['relations'],
        liked: false,
      };

      startTransition(async () => {
        setSubmitting(true);
        addOptimistic(pending);
        try {
          const realId = await createComment(sellerId, postId, text);
          // State update after `await` inside a transition must
          // re-enter one. Committing to the confirmed list right
          // before the transition settles avoids a revert flash.
          startTransition(() => {
            updateData(prev => [{ ...pending, id: realId }, ...prev]);
          });
          onCommentCreated?.();
          resolve();
        } catch (error) {
          console.error('[useOptimisticComments] create failed', error);
          reject(error);
        }
      });
    });

  return { comments, isInitializing, hasMore, loadMore, submit, isSubmitting };
}
